import {
  createIndexedDbStorage,
  createOutbox,
  memoryStorage,
  newOperationId as mintOperationId,
  type OfflineStorage,
} from '@/lib/offline';
import { api } from '@/lib/api';

/**
 * §28 / ADR-010 — the dashboard's wiring for the shared offline outbox.
 *
 * The queue itself lives in `@/lib/offline`: the durable store, the
 * `operationId`, the flush lock, the 4xx policy (401/408/429 stay queued, the
 * rest dead-letter), the retry cap and the dead letter are one implementation
 * shared by every client. The four mobile apps have had this for a while; the
 * dashboard did not, so a manager taking an order on a venue's weak back-office
 * Wi-Fi lost it to a red toast and no record at all. Only the three host ports —
 * storage, transport, reachability — are supplied here.
 *
 * Nothing ever leaves the browser without either reaching the server or landing
 * in the dead letter, where a person can see it. Online-required actions
 * (card payments, drawer open/close) must NOT go through the outbox — they set
 * `onlineOnly` and surface their error.
 */

/**
 * This app's slice of the origin.
 *
 * It names both the storage key prefix and — in the IndexedDB adapter — the
 * database, so the dashboard and customer-web served from the same origin
 * cannot land in one queue even though both would otherwise default to
 * `venue.outbox`.
 */
const NAMESPACE = 'venue.dashboard.outbox';

/**
 * Durable storage: IndexedDB, from the package's own adapter.
 *
 * Not `localStorage`. It is synchronous, capped near 5MB, and its writes are
 * not transactional, so a tab killed mid-write can leave a truncated string
 * that `JSON.parse` rejects — the whole queue, unreadable. The adapter is the
 * one place in the codebase that knows any of that, and there is deliberately
 * no second copy of it here.
 *
 * The factory is async and probes the store before answering, so it cannot be
 * handed straight to `createOutbox`. This facade defers to it on first use:
 * one construction, awaited by whichever call needs the disk first.
 */
let backing: Promise<OfflineStorage> | null = null;


function openStorage(): Promise<OfflineStorage> {
  backing ??= createIndexedDbStorage({ namespace: NAMESPACE }).catch((error: unknown) => {
    // Deliberately not a rethrow. Rejecting here would make every write fail in
    // a private window, which is stricter than this app was before the outbox
    // existed; the honest trade is to keep accepting work, in memory, and to
    // say so where an operator can see it.
    console.error('[outbox] IndexedDB unavailable — the queue is memory-only this session', error);
    return memoryStorage();
  });
  return backing;
}

const storage: OfflineStorage = {
  getItem: (key) => openStorage().then((disk) => disk.getItem(key)),
  setItem: (key, value) => openStorage().then((disk) => disk.setItem(key, value)),
  removeItem: (key) => openStorage().then((disk) => disk.removeItem(key)),
};

/**
 * Whether it is worth touching the network at all.
 *
 * `navigator.onLine === false` is the one reliable signal a browser gives: it
 * means there is no network interface at all. `true` only means an interface
 * exists — a captive portal or a dead uplink still reads as online — so it is
 * treated as "try it and let the request decide". Answering "offline" on a
 * maybe is the mistake that leaves a queue never flushing at all.
 */
async function isReachable(): Promise<boolean> {
  return typeof navigator === 'undefined' || navigator.onLine !== false;
}

/** Monotonic-ish, collision-resistant enough for a per-browser queue. */
export const newOperationId = (): string => mintOperationId();

export const outbox = createOutbox({
  storage,
  namespace: NAMESPACE,
  isReachable,
  newOperationId: () => newOperationId(),
  // The same axios client every read goes through, so the bearer token, the
  // tenant header, the 401 refresh and the envelope unwrapping are identical
  // for a queued write and a live one.
  transport: (request) => api.request(request),
});

/**
 * Drain on reconnect.
 *
 * `submit()` already flushes when the operator acts, so this only covers the
 * case where nobody is acting: the tab sat offline with work queued and the
 * link came back. Without it that work waits for the next click.
 */
if (typeof window !== 'undefined') {
  window.addEventListener('online', () => void outbox.flush());
}

export type { DeadLetter, ReplayKeyField } from '@/lib/offline';
